import { lazy, type ComponentType } from "react";
import { matchPath } from "react-router-dom";
import Index from "./pages/Index";
import { services } from "@/lib/services-data";
import { locations } from "@/lib/locations-data";
import { blogPosts } from "@/lib/blog-data";

type PageModule = { default: ComponentType };

export type PageComponent = ComponentType & { preload: () => Promise<unknown> };

/**
 * A code-split page that can also be loaded ahead of rendering.
 *
 * `React.lazy` alone suspends on first render, which the prerender
 * (`renderToString`) cannot wait for and which would make hydration replace the
 * server HTML with the Suspense fallback. Calling `preload()` first resolves the
 * module, after which the page renders synchronously like an eager import.
 */
function page(loader: () => Promise<PageModule>): PageComponent {
  let Loaded: ComponentType | null = null;
  let pending: Promise<unknown> | null = null;
  const Lazy = lazy(loader);
  const preload = () =>
    (pending ??= loader().then((m) => {
      Loaded = m.default;
    }));
  const Page = () => (Loaded ? <Loaded /> : <Lazy />);
  return Object.assign(Page, { preload });
}

/** The landing page stays in the entry chunk — it is the primary entry point. */
const eager = (C: ComponentType): PageComponent =>
  Object.assign(() => <C />, { preload: () => Promise.resolve() });

const NotFound = page(() => import("./pages/NotFound"));

export const routes: { path: string; Page: PageComponent }[] = [
  { path: "/", Page: eager(Index) },
  { path: "/services", Page: page(() => import("./pages/Services")) },
  { path: "/services/:slug", Page: page(() => import("./pages/ServiceDetail")) },
  { path: "/gallery", Page: page(() => import("./pages/Gallery")) },
  { path: "/blog", Page: page(() => import("./pages/Blog")) },
  { path: "/blog/:slug", Page: page(() => import("./pages/BlogPost")) },
  { path: "/about", Page: page(() => import("./pages/About")) },
  { path: "/contact", Page: page(() => import("./pages/Contact")) },
  { path: "/locations", Page: page(() => import("./pages/Locations")) },
  { path: "/locations/:slug", Page: page(() => import("./pages/LocationDetail")) },
  { path: "/admin/gallery-categorize", Page: page(() => import("./pages/AdminGalleryCategorize")) },
  // Keep custom routes above the catch-all.
  { path: "*", Page: NotFound },
];

/** The page component that will render for a pathname. */
export function pageFor(pathname: string): PageComponent {
  return routes.find((r) => r.path !== "*" && matchPath(r.path, pathname))?.Page ?? NotFound;
}

/** Every public URL, prerendered to its own static HTML file at build time. */
export const prerenderPaths: string[] = [
  "/",
  "/services",
  ...services.map((s) => `/services/${s.slug}`),
  "/locations",
  ...locations.map((l) => `/locations/${l.slug}`),
  "/gallery",
  "/blog",
  ...blogPosts.map((p) => `/blog/${p.slug}`),
  "/about",
  "/contact",
];
